import type { Dispatch, SetStateAction } from "react";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";

const COLOR_SCHEME_QUERY = "(prefers-color-scheme: dark)";

export type Theme = "system" | "dark" | "light";

/**
 * Repair a known bug: an earlier inline `<script>` (and some manual user-
 * agent steps) wrote `localStorage.theme = "dark"` as a bare string.
 * `usehooks-ts` JSON.parses on read, so the bare string crashes the
 * useState initializer with `Unexpected token 'd'`. Run once on module
 * load to coerce any non-JSON value into a JSON-encoded one.
 *
 * Safe to delete after a few weeks once we're confident every active
 * browser has migrated.
 */
const VALID_THEMES = new Set<Theme>(["system", "dark", "light"]);

const repairTheme = () => {
  if (typeof window === "undefined") return;
  // Some test runners (jsdom variants without storage) throw the moment
  // we touch `localStorage`. Wrap the whole block; if we can't read/write,
  // there's nothing to repair anyway.
  try {
    const raw = window.localStorage.getItem("theme");
    if (raw == null) return;
    try {
      JSON.parse(raw);
      return;
    } catch {
      // Bare string fallback: keep if it's a known theme value, else wipe.
      if (VALID_THEMES.has(raw as Theme)) {
        window.localStorage.setItem("theme", JSON.stringify(raw));
      } else {
        window.localStorage.removeItem("theme");
      }
    }
  } catch {
    // localStorage unavailable (SSR, sandboxed iframe, or test harness).
  }
};

repairTheme();

type UseThemeOutput = {
  theme: Theme;
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: Dispatch<SetStateAction<Theme>>;
};

export const useTheme = (): UseThemeOutput => {
  const isDarkOS = useMediaQuery(COLOR_SCHEME_QUERY);
  const [theme, setTheme] = useLocalStorage<Theme>("theme", "system");

  // Derived, not stored: a separate `isDarkMode` state seeded from the OS
  // preference rendered the wrong mode for a frame after every mount, and
  // the effects that "corrected" it raced with the server-backed settings.
  const isDarkMode = theme === "dark" || (theme === "system" && isDarkOS);

  function toggleTheme() {
    const toggleDict: Record<Theme, Theme> = {
      light: "system",
      system: "dark",
      dark: "light",
    };

    setTheme((prevMode) => toggleDict[prevMode]);
  }

  return {
    theme,
    setTheme,
    isDarkMode,
    toggleTheme,
  };
};
